import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { allPicked, toggled, withAll } from "./bulk";
import { shortTime } from "./calls";
import { go, useTitle } from "./hooks";
import { changedText, changeIds, MESSAGE_COLUMNS, MESSAGE_FILTERS, messageCount, readMessageFilter, STATUS_TEXT } from "./messages";
import { personName } from "./requests";
import { SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

type Load = { messages: api.CustomerMessage[]; limit: number; can_edit: boolean } | "server" | "network" | "session" | "lag" | undefined;

/** SO-1462: the messages of portal users, a status filter, and a status change of all the selected rows. */
export function MessagesPage({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const [filter, setFilterState] = useState<api.MessageFilter>(() => readMessageFilter(location.search));
  const [found, setFound] = useState<Load>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string; session?: boolean } | null>(null);
  const noteLine = useRef<HTMLParagraphElement>(null);
  const [sort, setSort] = useSort("sort", MESSAGE_COLUMNS);
  useTitle(`Customer messages · ${org.name}`);
  const to = filter === "done" ? "open" : "done";
  const setFilter = (next: api.MessageFilter) => {
    setFilterState(next);
    setPicked(new Set());
    setNote(null);
    const url = new URL(location.href);
    if (next === "open") url.searchParams.delete("status");
    else url.searchParams.set("status", next);
    history.replaceState(history.state, "", url);
  };
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    const started = performance.now();
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.customerMessages(token, filter))
      .then((list) => {
        console.info(`messages: ${list.messages.length} loaded in ${Math.round(performance.now() - started)} ms`);
        if (live) setFound(list);
      })
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        console.warn("messages: load failed", code);
        if (live) setFound(code === 401 ? "session" : code === 404 ? "lag" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, filter, attempt, canView]);
  if (canView === false) {
    return (
      <main data-testid="messages-hidden">
        <h1 tabIndex={-1}>Customer messages</h1>
        <p>Your role does not show customer messages.</p>
      </main>
    );
  }
  const rows = typeof found === "object" ? sortRows(found.messages, MESSAGE_COLUMNS, sort) : [];
  const selectable = changeIds(rows, to);
  const pickedIds = selectable.filter((id) => picked.has(id));
  const change = async () => {
    setSaving(true);
    setNote(null);
    const started = performance.now();
    try {
      const done = await api.setMessageStatus(await user.getIdToken(), pickedIds, to);
      console.info(`messages: ${done.changed} changed to ${to} in ${Math.round(performance.now() - started)} ms`);
      setNote({ ok: true, text: changedText(to, done.changed, done.unchanged) });
      setPicked(new Set());
      setAttempt((n) => n + 1);
    } catch (e) {
      const failure = saveFailure(e, "The change");
      setNote({ ok: false, text: failure.text, session: !failure.ok && failure.session });
    } finally {
      setSaving(false);
      requestAnimationFrame(() => noteLine.current?.focus());
    }
  };
  return (
    <main className="issues-page wide" data-testid="messages-page">
      <h1 tabIndex={-1}>Customer messages</h1>
      <p className="sub">Messages that restaurants send from the Support tab of the customer portal, about one Location each.</p>
      <div className="tabs" role="group" aria-label="Which messages">
        {MESSAGE_FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={filter === option.value}
            data-testid={`messages-${option.value}`}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <p ref={noteLine} tabIndex={-1} role="status" data-testid="messages-note" className={note && !note.ok ? "error" : undefined}>
        {note?.text}
        {note?.session && (
          <>
            {" "}
            <button type="button" onClick={() => signOut(auth)}>
              Sign in again
            </button>
          </>
        )}
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : found === "lag" ? (
        <p data-testid="messages-lag">This page is not open yet. Try again later.</p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="messages-none">No customer messages with this status.</p>
      ) : (
        <>
          {rows.length >= found.limit && (
            <p className="sub" data-testid="messages-limit">
              The list shows the newest {found.limit} messages.
            </p>
          )}
          {found.can_edit && filter !== "all" && selectable.length > 0 && (
            <p className="bulk-bar" data-testid="messages-bulk">
              <label className="check">
                <input
                  type="checkbox"
                  data-testid="messages-pick-all"
                  checked={allPicked(picked, selectable)}
                  onChange={(e) => setPicked((before) => withAll(before, selectable, e.target.checked))}
                />{" "}
                Select all ({selectable.length})
              </label>{" "}
              <span data-testid="messages-count">{pickedIds.length} selected</span>{" "}
              <button type="button" data-testid="messages-change" disabled={pickedIds.length === 0 || saving} onClick={change}>
                {saving ? "Saving…" : to === "done" ? `Mark ${messageCount(pickedIds.length)} done` : `Open ${messageCount(pickedIds.length)} again`}
              </button>
              {pickedIds.length > 0 && (
                <>
                  {" "}
                  <button type="button" className="quiet" data-testid="messages-clear" onClick={() => setPicked(new Set())}>
                    Clear the selection
                  </button>
                </>
              )}
            </p>
          )}
          <SortSelect id="messages-sort" columns={MESSAGE_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Customer messages">
            <table className="list requests-table messages-table" data-testid="messages-table" data-cols="messages-sort">
              <thead>
                <tr>
                  {MESSAGE_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((message) => (
                  <tr key={message.id} data-testid="message-row" data-status={message.status} className={picked.has(message.id) ? "picked" : undefined}>
                    <th scope="row" className="account-number">
                      {found.can_edit && filter !== "all" && selectable.includes(message.id) && (
                        <label className="pick-target">
                          <input
                            type="checkbox"
                            className="pick"
                            data-testid="message-pick"
                            aria-label={`Select the message of ${message.account_number}`}
                            checked={picked.has(message.id)}
                            onChange={() => setPicked((before) => toggled(before, message.id))}
                          />
                        </label>
                      )}
                      <a href={`${base}/accounts/${message.account_id}`} onClick={go}>
                        {message.account_number}
                      </a>
                      {message.account_name !== message.location && (
                        <>
                          {" "}
                          <span className="sub clip name" title={message.account_name}>
                            {message.account_name}
                          </span>
                        </>
                      )}
                    </th>
                    <td data-label="Sent" className="sent">
                      {shortTime(message.sent_at, org.time_zone)}
                    </td>
                    <td data-label="Location" className="location">
                      {message.location ?? "–"}
                    </td>
                    <td data-label="From" className="from" data-testid="message-from">
                      {message.from_email ? (
                        <a className="clip short" href={`mailto:${message.from_email}`} title={message.from_email} aria-label={message.from_email}>
                          {personName(message.from_email)}
                        </a>
                      ) : (
                        "–"
                      )}
                    </td>
                    <td data-label="Message" className="wrap">
                      {message.text}
                    </td>
                    <td data-label="Status">
                      <span className={message.status === "open" ? "chip-flag" : "chip-flag neutral"}>{STATUS_TEXT[message.status]}</span>
                    </td>
                    <td data-label="Done by" className="done-by">
                      {message.done_by ? (
                        <span className="clip short" title={message.done_by}>
                          {personName(message.done_by)}
                        </span>
                      ) : (
                        "–"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}
